// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Printer, Loader2, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';


type SortField =
  | 'division'
  | 'team_name'
  | 'checkin_location'
  | 'head_coach_name'
  | 'head_coach_phone'
  | 'ac_name_override'
  | 'ac_phone_override';
type SortDir = 'asc' | 'desc';

const AGE_TABS = ['All', '11U', '12U', '13U', '14U', '15U', '16U', '17U'];

function extractAge(division: string | null): string {
  if (!division) return '';
  const match = String(division).match(/\b(\d{1,2})[uU]\b/);
  return match ? `${match[1]}U` : '';
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
  { value: 'comp', label: 'Comp' },
];

const methodLabel = (m: string | null) =>
  PAYMENT_METHODS.find((p) => p.value === (m || '').toLowerCase())?.label || (m || 'Manual');


const fmtMoney = (n: any) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

interface PaymentCellProps {
  row: any;
  readOnly: boolean;
  onMarkPaid: (rid: string, opts: { method: string; amount?: number; compReason?: string }) => Promise<boolean>;
}

const PaymentCell: React.FC<PaymentCellProps> = ({ row, readOnly, onMarkPaid }) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(String(Number(row.balance || 0).toFixed(2)));
  const [compReason, setCompReason] = useState('');
  const [saving, setSaving] = useState(false);

  if (row.payment_status === 'comped') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-blue-700 font-semibold cursor-help">Comped</span>
          </TooltipTrigger>
          <TooltipContent>{row.comp_reason || 'Comped'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (row.payment_status === 'paid' || Number(row.balance) <= 0) {
    return (
      <span className="text-green-700 font-semibold whitespace-nowrap">
        Paid · {methodLabel(row.payment_received_method)}
      </span>
    );
  }

  const handleSave = async () => {
    if (method === 'comp' && !compReason.trim()) return;
    setSaving(true);
    const ok = await onMarkPaid(row.registration_id, {
      method,
      amount: method === 'comp' ? undefined : Number(amount),
      compReason: method === 'comp' ? compReason : undefined,
    });
    setSaving(false);
    if (ok) setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-amber-700 whitespace-nowrap">Bal {fmtMoney(row.balance)}</span>
      {!readOnly && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2">
              Mark Paid
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 space-y-3" align="end">
            <div className="space-y-1.5">
              <Label className="text-xs">Payment method</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            {method !== 'comp' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="h-8 text-sm"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            )}
            {method === 'comp' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Comp reason <span className="text-red-500">*</span></Label>
                <Textarea
                  className="text-sm min-h-[60px]"
                  placeholder="Reason for comp..."
                  value={compReason}
                  onChange={(e) => setCompReason(e.target.value)}
                />
              </div>
            )}
            <Button
              size="sm"
              className="w-full h-8"
              disabled={saving || (method === 'comp' && !compReason.trim())}
              onClick={handleSave}
            >
              {saving ? 'Saving...' : method === 'comp' ? 'Mark Comped' : 'Mark Paid'}
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

const fmtDate = (s: any) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

interface WaiverCellProps {
  row: any;
  settings: any;
  blockUnsigned: boolean;
  signLink: string;
}

const WaiverCell: React.FC<WaiverCellProps> = ({ row, settings, blockUnsigned, signLink }) => {
  const teamReq = !!settings?.team_waiver_required;
  const indReq = !!settings?.individual_waiver_required;
  const teamSigned = !!row.team_waiver;
  const rosterSize = Number(row.roster_size || 0);
  const indSigned = Number(row.individual_signed || 0);
  const indComplete = indSigned >= rosterSize && rosterSize > 0;

  const met =
    (!teamReq || teamSigned) && (!indReq || indComplete);
  const flagRed = blockUnsigned && !met;

  const copyLink = async () => {
    if (!signLink) return;
    try {
      await navigator.clipboard.writeText(signLink);
      toast.success('Sign link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-left w-full">
          {teamReq && (
            <span
              className={`block text-xs font-semibold ${
                teamSigned ? 'text-green-700' : flagRed ? 'text-red-600' : 'text-amber-700'
              }`}
            >
              {teamSigned ? 'Signed' : 'Needed'}
            </span>
          )}
          {indReq && (
            <span
              className={`block text-xs font-semibold ${
                indComplete ? 'text-green-700' : flagRed ? 'text-red-600' : 'text-amber-700'
              }`}
            >
              {indSigned}/{rosterSize}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="start">
        <div className="text-sm font-semibold">Waiver status</div>
        {teamReq && (
          <div className="text-xs">
            Team waiver:{' '}
            {teamSigned ? (
              <span className="text-green-700">
                Signed by {row.team_waiver.signer_name || 'team'}
                {row.team_waiver.signed_at ? ` · ${fmtDate(row.team_waiver.signed_at)}` : ''}
              </span>
            ) : (
              <span className="text-amber-700">Not signed yet</span>
            )}
          </div>
        )}
        {indReq && (
          <div className="text-xs">
            Individual waivers: {indSigned} of {rosterSize} signed
            {!indComplete && (
              <span className="text-amber-700"> · {rosterSize - indSigned} still needed</span>
            )}
          </div>
        )}
        {signLink && (
          <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={copyLink}>
            Copy sign link
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

interface ExtrasCellProps {
  row: any;
  readOnly: boolean;
  onToggle: (extraId: string, redeemed: boolean) => void;
}

const ExtrasCell: React.FC<ExtrasCellProps> = ({ row, readOnly, onToggle }) => {
  const extras: any[] = row.extras || [];
  if (extras.length === 0) return <span className="text-muted-foreground">—</span>;
  const given = extras.filter((e) => e.redeemed).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-xs underline-offset-2 hover:underline">
          {given}/{extras.length} given
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="start">
        <div className="text-sm font-semibold">Purchased extras</div>
        {extras.map((ex) => (
          <label key={ex.id} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={!!ex.redeemed}
              disabled={readOnly}
              onCheckedChange={(checked) => onToggle(ex.id, !!checked)}
            />
            <span className="text-xs">
              {ex.name}
              {ex.player_name ? <span className="text-muted-foreground"> · {ex.player_name}</span> : ''}
            </span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
};

interface Props {
  /** Authenticated staff path */
  eventId?: string;

  /** Tokenized share path */
  token?: string;
  label?: string;
  /** Force read-only regardless of permission */
  readOnly?: boolean;
}

const CheckinSummaryPublicView: React.FC<Props> = ({ eventId, token, label, readOnly: readOnlyProp }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('All');
  const [editingCell, setEditingCell] = useState<{ rid: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [sortField, setSortField] = useState<SortField>('division');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [printDrawerOpen, setPrintDrawerOpen] = useState(false);
  const [printTab, setPrintTab] = useState<string | null>(null);

  const queryKey = ['checkin-grid', token || eventId];

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!(token || eventId),
    queryFn: async () => {
      const { data: res, error } = await supabase.rpc('checkin_grid_load', {
        p_token: token ?? null,
        p_event_id: eventId ?? null,
      });
      if (error) throw error;
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      if (parsed?.error) throw new Error(parsed.error);
      return parsed;
    },
  });

  const permission = data?.permission || 'view';
  const readOnly = readOnlyProp || permission !== 'edit';
  const eventInfo = data?.event;
  const resolvedEventId = data?.event_id || eventId;
  const courts: string[] = data?.courts || [];
  const allRows = data?.rows || [];

  // Build the venue dropdown list from the event's venue fields
  const { data: venueOptions = [] } = useQuery({
    queryKey: ['checkin-venues', resolvedEventId],
    enabled: !!resolvedEventId,
    queryFn: async () => {
      const { data: ev } = await supabase
        .from('events')
        .select('main_venue_id, venue_id, additional_venue_ids')
        .eq('id', resolvedEventId)
        .maybeSingle();
      if (!ev) return [];
      const ids: string[] = [];
      if (ev.main_venue_id) ids.push(ev.main_venue_id);
      if (ev.venue_id) ids.push(ev.venue_id);
      if (Array.isArray(ev.additional_venue_ids)) ids.push(...ev.additional_venue_ids.filter(Boolean));
      const uniqueIds = Array.from(new Set(ids));
      if (uniqueIds.length === 0) return [];
      const { data: venues } = await supabase
        .from('venues')
        .select('id, name, city')
        .in('id', uniqueIds);
      if (!venues) return [];
      // Order venues to match id order, de-dupe by id
      const byId = new Map(venues.map((v: any) => [v.id, v]));
      const ordered = uniqueIds.map((id) => byId.get(id)).filter(Boolean);
      // Detect duplicate names to decide whether to append city
      const nameCounts: Record<string, number> = {};
      ordered.forEach((v: any) => {
        nameCounts[v.name] = (nameCounts[v.name] || 0) + 1;
      });
      const labels = new Set<string>();
      ordered.forEach((v: any) => {
        const label = nameCounts[v.name] > 1 && v.city ? `${v.name}, ${v.city}` : v.name;
        labels.add(label);
      });
      return Array.from(labels);
    },
  });

  const waiverSettings = data?.waiver_settings || null;
  const showWaiverColumn = !!(waiverSettings?.team_waiver_required || waiverSettings?.individual_waiver_required);
  const blockUnsigned = !!waiverSettings?.block_unsigned;

  const waiverMet = useCallback(
    (row: any) => {
      if (!showWaiverColumn) return true;
      if (waiverSettings?.team_waiver_required && !row.team_waiver) return false;
      if (
        waiverSettings?.individual_waiver_required &&
        Number(row.individual_signed || 0) < Number(row.roster_size || 0)
      )
        return false;
      return true;
    },
    [showWaiverColumn, waiverSettings]
  );

  const signLink = useMemo(() => {
    const slug = eventInfo?.slug || eventInfo?.id;
    return slug ? `${window.location.origin}/events/${slug}` : '';
  }, [eventInfo]);


  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const writeCell = useCallback(
    async (rid: string, patch: Record<string, any>) => {
      const { data: res, error } = await supabase.rpc('checkin_grid_write', {
        p_event_id: resolvedEventId,
        p_registration_id: rid,
        p_patch: patch,
        p_token: token ?? null,
      });
      if (error || res?.error) {
        toast.error('Save failed');
        return false;
      }
      invalidate();
      return true;
    },
    [resolvedEventId, token]
  );

  const saveEdit = useCallback(
    async (rid: string, field: string, value: string) => {
      if (readOnly) return;
      await writeCell(rid, { [field]: value || null });
      setEditingCell(null);
    },
    [readOnly, writeCell]
  );

  const markPaid = async (
    rid: string,
    opts: { method: string; amount?: number; compReason?: string }
  ) => {
    if (readOnly) return false;
    const { data: res, error } = await supabase.rpc('checkin_mark_paid', {
      p_event_id: resolvedEventId,
      p_registration_id: rid,
      p_token: token ?? null,
      p_method: opts.method,
      p_amount: opts.amount ?? null,
      p_comp_reason: opts.compReason ?? null,
    });
    if (error || res?.error) {
      toast.error(res?.error === 'comp_reason_required' ? 'Comp reason is required' : 'Failed to mark paid');
      return false;
    }
    toast.success(opts.method === 'comp' ? 'Marked comped' : 'Marked paid');
    invalidate();
    return true;
  };


  const toggleExtra = async (extraId: string, redeemed: boolean) => {
    if (readOnly) return;
    const { data: res, error } = await supabase.rpc('checkin_set_extra_redeemed', {
      p_event_id: resolvedEventId,
      p_extra_id: extraId,
      p_redeemed: redeemed,
      p_token: token ?? null,
    });
    if (error || res?.error) toast.error('Failed to update extra');
    else invalidate();
  };

  const displayRows = useMemo(() => {
    let rows = [...allRows];
    if (activeTab !== 'All') {
      rows = rows.filter((r: any) => extractAge(r.division) === activeTab);
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter(
        (r: any) =>
          String(r.team_name || '').toLowerCase().includes(q) ||
          String(r.head_coach_name || '').toLowerCase().includes(q) ||
          String(r.checkin_location || '').toLowerCase().includes(q)
      );
    }
    return rows.sort((a: any, b: any) => {
      const cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allRows, activeTab, debouncedSearch, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1 text-primary" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1 text-primary" />
    );
  };

  const startEdit = (rid: string, field: string, currentValue: string) => {
    if (readOnly) return;
    setEditingCell({ rid, field });
    setEditValue(currentValue || '');
  };

  const availableAgeGroups = useMemo(() => {
    const groups = new Set<string>();
    allRows.forEach((r: any) => {
      const age = extractAge(r.division);
      if (age) groups.add(age);
    });
    return AGE_TABS.filter((t) => t === 'All' || groups.has(t));
  }, [allRows]);

  const handlePrintAgeGroup = (tab: string) => {
    setPrintDrawerOpen(false);
    const previousTab = activeTab;
    setActiveTab(tab);
    setPrintTab(tab === 'All' ? null : tab);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setActiveTab(previousTab);
        setPrintTab(null);
      }, 500);
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        This check-in sheet is unavailable.
      </div>
    );
  }

  const editableTextCell = (row: any, field: string, opts: { bold?: boolean; phone?: boolean } = {}) => (
    <TableCell
      className={`${!readOnly ? 'cursor-pointer hover:bg-muted/50' : ''} ${opts.bold ? 'font-bold text-sm' : ''}`}
      onClick={() => startEdit(row.registration_id, field, row[field])}
    >
      {editingCell?.rid === row.registration_id && editingCell.field === field ? (
        <Input
          autoFocus
          className="h-6 text-xs px-1 py-0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEdit(row.registration_id, field, editValue)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit(row.registration_id, field, editValue);
            if (e.key === 'Escape') setEditingCell(null);
          }}
        />
      ) : (
        <span className={row[field] ? '' : 'text-muted-foreground'}>{row[field] || '—'}</span>
      )}
    </TableCell>
  );

  return (
    <div id="checkin-print-root" className="flex flex-col h-screen">
      <style>{`
        @media print {
          @page { size: landscape; margin: 0.3in; }
          body * { visibility: hidden; }
          #checkin-print-root, #checkin-print-root * { visibility: visible; }
          #checkin-print-root { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-sig-line { display: block !important; border-bottom: 2px solid #000 !important; min-width: 100px; height: 20px; }
        }
        .print-sig-line { display: none; }
      `}</style>

      {/* Toolbar */}
      <div className="no-print px-4 py-3 flex items-center gap-3 border-b bg-[#002E62]">
        <h1 className="text-lg font-bold text-white whitespace-nowrap">{label || 'Check-In Sheet'}</h1>
        <span className="text-white/60 text-sm whitespace-nowrap">
          {eventInfo?.name} — {activeTab !== 'All' ? activeTab : 'All'} — {displayRows.length} teams
        </span>
        {readOnly && (
          <span className="text-white/40 text-xs bg-white/10 px-2 py-0.5 rounded">View only</span>
        )}
        <div className="flex-1" />
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
          <Input
            placeholder="Search teams, coaches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-8 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20"
          />
        </div>
        <Button size="sm" variant="secondary" onClick={() => setPrintDrawerOpen(true)}>
          <Printer className="h-4 w-4 mr-2" /> Print
        </Button>
      </div>

      {/* Age group tabs */}
      <div className="no-print border-b bg-muted/30 flex items-center overflow-x-auto shrink-0">
        {availableAgeGroups.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 text-xs font-semibold border-r whitespace-nowrap transition-colors ${
              activeTab === tab
                ? 'bg-background text-foreground border-b-2 border-b-[#002E62]'
                : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table className="text-xs">
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="bg-muted/80 backdrop-blur">
              <TableHead className="w-[36px] text-center">#</TableHead>
              <TableHead className="min-w-[80px] cursor-pointer select-none" onClick={() => toggleSort('division')}>
                <span className="flex items-center">Division <SortIcon field="division" /></span>
              </TableHead>
              <TableHead className="min-w-[160px] cursor-pointer select-none" onClick={() => toggleSort('team_name')}>
                <span className="flex items-center">Team Name <SortIcon field="team_name" /></span>
              </TableHead>
              <TableHead className="min-w-[130px] cursor-pointer select-none print:hidden" onClick={() => toggleSort('checkin_location')}>
                <span className="flex items-center">Check-In Location <SortIcon field="checkin_location" /></span>
              </TableHead>
              <TableHead className="min-w-[50px] text-center print:hidden">✓ In</TableHead>
              <TableHead className="min-w-[120px]">HC Signature</TableHead>
              <TableHead className="min-w-[130px] cursor-pointer select-none" onClick={() => toggleSort('head_coach_name')}>
                <span className="flex items-center">Head Coach <SortIcon field="head_coach_name" /></span>
              </TableHead>
              <TableHead className="min-w-[110px] print:hidden">HC Phone</TableHead>
              <TableHead className="min-w-[130px] cursor-pointer select-none" onClick={() => toggleSort('ac_name_override')}>
                <span className="flex items-center">Asst Coach <SortIcon field="ac_name_override" /></span>
              </TableHead>
              <TableHead className="min-w-[50px] text-center print:hidden">✓ AC</TableHead>
              <TableHead className="min-w-[120px]">AC Signature</TableHead>
              <TableHead className="min-w-[110px] print:hidden">AC Phone</TableHead>
              {showWaiverColumn && <TableHead className="min-w-[90px] print:hidden">Waiver</TableHead>}
              <TableHead className="min-w-[140px] print:hidden">Payment</TableHead>
              <TableHead className="min-w-[160px] print:hidden">Extras</TableHead>

            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((row: any, i: number) => {
              const hcName = row.hc_name_override || row.head_coach_name || '';
              const hcPhone = row.hc_phone_override || row.head_coach_phone || '';
              const extras: any[] = row.extras || [];
              const given = extras.filter((e) => e.redeemed).length;
              return (
                <TableRow key={row.registration_id} className="hover:bg-muted/30">
                  <TableCell className="text-center text-muted-foreground font-mono">{i + 1}</TableCell>
                  <TableCell className="font-medium">{row.division || '—'}</TableCell>
                  <TableCell className="font-bold text-sm">{row.team_name}</TableCell>

                  {/* Check-In Location dropdown */}
                  <TableCell
                    className={`${!readOnly ? 'cursor-pointer hover:bg-muted/50' : ''} print:hidden`}
                    onClick={() => startEdit(row.registration_id, 'checkin_location', row.checkin_location)}
                  >
                    {editingCell?.rid === row.registration_id && editingCell.field === 'checkin_location' ? (
                      <select
                        autoFocus
                        className="w-full border rounded px-1 py-0.5 text-xs bg-background"
                        value={editValue}
                        onChange={(e) => {
                          setEditValue(e.target.value);
                          saveEdit(row.registration_id, 'checkin_location', e.target.value);
                        }}
                        onBlur={() => setEditingCell(null)}
                      >
                        <option value="">—</option>
                        {venueOptions.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}

                      </select>
                    ) : (
                      <span className={row.checkin_location ? '' : 'text-muted-foreground'}>
                        {row.checkin_location || '—'}
                      </span>
                    )}
                  </TableCell>

                  {/* ✓ In */}
                  <TableCell className="text-center print:hidden">
                    {blockUnsigned && !waiverMet(row) && !row.checked_in ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Checkbox checked={false} disabled className="mx-auto opacity-40" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Waiver must be signed before this team can check in.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Checkbox
                        checked={!!row.checked_in}
                        disabled={readOnly}
                        onCheckedChange={(checked) => writeCell(row.registration_id, { checked_in: !!checked })}
                        className="mx-auto"
                      />
                    )}
                  </TableCell>


                  {/* HC Signature */}
                  <TableCell className="text-center">
                    <div className="print:hidden">
                      <Checkbox
                        checked={!!row.hc_signature}
                        disabled={readOnly}
                        onCheckedChange={(checked) => writeCell(row.registration_id, { hc_signature: !!checked })}
                        className="mx-auto"
                      />
                      {row.hc_signature && <span className="text-[10px] text-muted-foreground block">On File</span>}
                    </div>
                    <div className="hidden print:block print-sig-line" />
                  </TableCell>

                  {/* Head Coach (override editable) */}
                  <TableCell
                    className={`${!readOnly ? 'cursor-pointer hover:bg-muted/50' : ''} font-bold text-sm`}
                    onClick={() => startEdit(row.registration_id, 'hc_name_override', hcName)}
                  >
                    {editingCell?.rid === row.registration_id && editingCell.field === 'hc_name_override' ? (
                      <Input
                        autoFocus
                        className="h-6 text-xs px-1 py-0"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(row.registration_id, 'hc_name_override', editValue)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(row.registration_id, 'hc_name_override', editValue);
                          if (e.key === 'Escape') setEditingCell(null);
                        }}
                      />
                    ) : (
                      <span className={hcName ? '' : 'text-muted-foreground'}>{hcName || '—'}</span>
                    )}
                  </TableCell>

                  {/* HC Phone */}
                  <TableCell
                    className={`${!readOnly ? 'cursor-pointer hover:bg-muted/50' : ''} print:hidden`}
                    onClick={() => startEdit(row.registration_id, 'hc_phone_override', hcPhone)}
                  >
                    {editingCell?.rid === row.registration_id && editingCell.field === 'hc_phone_override' ? (
                      <Input
                        autoFocus
                        className="h-6 text-xs px-1 py-0"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(row.registration_id, 'hc_phone_override', editValue)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(row.registration_id, 'hc_phone_override', editValue);
                          if (e.key === 'Escape') setEditingCell(null);
                        }}
                      />
                    ) : (
                      <span className={hcPhone ? '' : 'text-muted-foreground'}>{hcPhone || '—'}</span>
                    )}
                  </TableCell>

                  {/* Asst Coach name */}
                  {editableTextCell(row, 'ac_name_override', { bold: true })}

                  {/* ✓ AC present */}
                  <TableCell className="text-center print:hidden">
                    <Checkbox
                      checked={!!row.ac_present}
                      disabled={readOnly}
                      onCheckedChange={(checked) => writeCell(row.registration_id, { ac_present: !!checked })}
                      className="mx-auto"
                    />
                  </TableCell>

                  {/* AC Signature */}
                  <TableCell className="text-center">
                    <div className="print:hidden">
                      <Checkbox
                        checked={!!row.ac_signature}
                        disabled={readOnly}
                        onCheckedChange={(checked) => writeCell(row.registration_id, { ac_signature: !!checked })}
                        className="mx-auto"
                      />
                      {row.ac_signature && <span className="text-[10px] text-muted-foreground block">On File</span>}
                    </div>
                    <div className="hidden print:block print-sig-line" />
                  </TableCell>

                  {/* AC Phone */}
                  {editableTextCell(row, 'ac_phone_override')}

                  {/* Waiver */}
                  {showWaiverColumn && (
                    <TableCell className="print:hidden">
                      <WaiverCell
                        row={row}
                        settings={waiverSettings}
                        blockUnsigned={blockUnsigned}
                        signLink={signLink}
                      />
                    </TableCell>
                  )}

                  {/* Payment */}
                  <TableCell className="print:hidden">
                    <PaymentCell row={row} readOnly={readOnly} onMarkPaid={markPaid} />
                  </TableCell>

                  {/* Extras */}
                  <TableCell className="print:hidden">
                    <ExtrasCell row={row} readOnly={readOnly} onToggle={toggleExtra} />
                  </TableCell>

                </TableRow>
              );
            })}
            {displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showWaiverColumn ? 15 : 14} className="text-center py-8 text-muted-foreground">
                  No teams found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={printDrawerOpen} onOpenChange={setPrintDrawerOpen}>
        <SheetContent side="right" className="w-[320px] sm:w-[380px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Print Check-In Sheet
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-2">
            <p className="text-sm text-muted-foreground mb-4">Select an age group to print.</p>
            {availableAgeGroups.map((tab) => {
              const count =
                tab === 'All'
                  ? allRows.length
                  : allRows.filter((r: any) => extractAge(r.division) === tab).length;
              return (
                <Button
                  key={tab}
                  variant="outline"
                  className="w-full justify-between h-12 text-sm"
                  onClick={() => handlePrintAgeGroup(tab)}
                >
                  <span className="font-semibold">{tab === 'All' ? 'All Age Groups' : tab}</span>
                  <span className="text-muted-foreground text-xs">{count} teams</span>
                </Button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CheckinSummaryPublicView;
